import { Component, OnInit, ViewChild } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';

import {
	Company,
  Reservation,
  BuildingRegion,
  ReservationRule,
  ReservationDailyRule,
  ReservationDayException,
  ReservationAvailability
} from '../shared/models';

import { ApiService } from '../shared/services/api.service';
import { StorageService } from '../shared/services/storage.service';
import { PageAccessService } from '../shared/services/page-access.service';

import { Utils } from '../shared/helpers/utils';
import { Constants } from '../shared/helpers/constants';
import { environment } from '../../environments/environment';

import { defineLocale } from 'ngx-bootstrap/chronos';
import { BsLocaleService, BsDatepickerConfig } from 'ngx-bootstrap/datepicker';
import { ptBrLocale } from 'ngx-bootstrap/locale';

import { faWhatsapp, faSpinner, faUtensils } from '../icons';

defineLocale('pt-br', ptBrLocale); 

@Component({
    selector: 'app-reservations',
    templateUrl: './reservations.component.html',
    styleUrls: ['./reservations.component.scss'],
    standalone: false
})
export class ReservationsComponent implements OnInit {
	done: boolean;
	// The master refused the page (plan's monthly visits, or late payment): what the customer reads.
	blockedMessage: string | null = null;
  logoPath: string;
  serverUrl: string = environment.server_url;
  bsConfig: BsDatepickerConfig = {
    showWeekNumbers: false
  } as BsDatepickerConfig;
  wappUrl: string = "";

  ////////////////// DATA LOADED //////////////////////
  company: Company;
  buildingRegions: BuildingRegion[];
  reservationRule: ReservationRule;
  reservationDailyRules: ReservationDailyRule[];
  reservationDayExceptions: ReservationDayException[];

  availabilities: ReservationAvailability[];
  /////////////////////////////////////////////////////

  ////////////////// DATA CALCULATED //////////////////
  today: Date = new Date();
  maxDate: Date = new Date();
  curDailyRule: ReservationDailyRule;
  curExceptions: ReservationDayException[];
  timeAvailabilities: ReservationAvailability[];
  curAvailability: ReservationAvailability;
  availSchedules: {time: string, disabled: boolean}[] = [];
  availPeopleQty: number[] = [];
  errors: {
    field: 'day'|'time'|'region',
    message: string,
    availability: ReservationAvailability
  };
  sending: boolean = false;
  /////////////////////////////////////////////////////

  ////////////////// USER INPUT //// //////////////////
  selectedDay: Date = new Date();
  selectedTime: string = '';
  tacAccepted: boolean = false;
  reservation: Reservation = {
    people_qty: 0,
    building_region_id: 0,
    customer_name: '',
    customer_phone: ''
  } as Reservation;
  /////////////////////////////////////////////////////

  ////////////////// ICONS ////////////////////////////
  faWhatsapp = faWhatsapp;
  faSpinner = faSpinner;
  faUtensils = faUtensils;
  /////////////////////////////////////////////////////


  constructor(private localeServ: BsLocaleService,
              private router: Router,
              private route: ActivatedRoute,
              private api: ApiService,
              private stor: StorageService,
              private pageAccess: PageAccessService) { }

  ngOnInit(): void {
    this.localeServ.use('pt-br');
    this.maxDate.setDate(this.maxDate.getDate() + 30); // can reserve for up to a month from now
    this.route.paramMap.subscribe(
      res => {
        if(!res["params"]["company_name"]) {
          alert("Nome inválido do estabelecimento");
          return;
        }
        const name = res["params"]["company_name"];
        this.pageAccess.check(name, 'reservations').subscribe(blocked => {
          this.blockedMessage = blocked;
          if (blocked) {
            this.done = true;
          } else {
            this._loadData(name);
          }
        });
      }
    );
  }

  onDateSelection(date: Date) {
    this.selectedDay = date;

    ////////////// RESETTING CHOSEN OPTIONS //////////////////
    this.selectedTime = '';
    this.reservation.people_qty = 0;
    this.reservation.building_region_id = 0;
    this.reservation.schedule = this.curAvailability = this.curDailyRule = null;
    this.availPeopleQty = [];
    BuildingRegion.setArrayAdditionalData(this.buildingRegions);
    //////////////////////////////////////////////////////////

    if(this.reservationDailyRules) {
      this.curDailyRule = ReservationDailyRule.getCurrent(this.reservationDailyRules, this.selectedDay);
      this._loadAvailabilities(Utils.dateToISO(date));
    }
    if(this.reservationDayExceptions) {
      this.curExceptions = ReservationDayException.getCurrent(this.reservationDayExceptions, this.selectedDay);
    }
    this.scrollDown(100);
  }
  timeChanged(event) {
    ////////////// RESETTING CHOSEN OPTIONS //////////////////
    this.reservation.people_qty = 0;
    this.reservation.building_region_id = 0;
    BuildingRegion.setArrayAdditionalData(this.buildingRegions);
    //////////////////////////////////////////////////////////

    if(!!event.target.value) {
      let time = event.target.value + ":00";
      this.reservation.schedule = Utils.dateToISO(this.selectedDay).split(" ")[0] + " " + time;
      let avails = [];
      for(let avail of this.availabilities) {
        if(avail.schedule == this.reservation.schedule) {
          avails.push(avail);
        }
      }
      this._setTimeAvailabilities(avails);
      this.scrollDown();
    }
  }
  regionChanged(event) {
    if(!!event.target.value) {
      for(let avail of this.timeAvailabilities) {
        if(event.target.value == avail.building_region.id) {
          this._setAvailability(avail);
        }
      }
      this.scrollDown();
    }
  }
  peopleQtyChanged(event) {
    if(!!event.target.value) {
      this.scrollDown(500);
    }
  }

  scrollDown(qty: number = 200, timeout: number = 300) {
    setTimeout(() => {
      window.scrollBy({
        top: qty,
        behavior: 'smooth'
      });
    }, timeout);
  }
  openWapp() {
    if(this.wappUrl) window.open(this.wappUrl, '_blank', 'noopener,noreferrer');

    setTimeout(() => {
      this.router.navigate(['..', 'minhas-reservas'], {relativeTo: this.route});
    }, 5000);
  }
  dontOpenWapp() {
    this.router.navigate(['..', 'minhas-reservas'], {relativeTo: this.route});
  }


  sendReservation() {
    this.sending = true;

    this.reservation.company_id = this.company.id;
    this.api.create('reservations', {
      reservation: this.reservation
    }).subscribe(
      res => {
        let reservation = res.reservation;
        reservation.companyName = this.company.name;
        this._genWappUrl(reservation);
        this.reservation.id = reservation.id;

        let allReservations = this.stor.getInLocal('reservations');
        if(allReservations) {
          for(let i = 0; i < allReservations.length; i++) {
            let reservation = allReservations[i];
            let rDate = new Date(reservation.schedule);
            if(rDate.getTime() < (new Date().getTime() + 1000*60*60*24*7)) {
              allReservations.splice(i, 1);
              i--;
            }
          }
          allReservations.push(reservation);
        } else {
          allReservations = [reservation];
        }
        this.stor.storeInLocal('reservations', allReservations);
        this.sending = false;
      },
      err => {
        console.log(err);
        alert("Erro ao enviar pedido de reserva");
        this.sending = false;
      }
    );
  }


  private _genWappUrl(reservation: Reservation) {
    if(!reservation || !reservation.id) {
      console.error("ReservationsComponent::genWappUrl -> no valid ID for reservation");
      return;
    }
    const phone = environment.ncommerce_wapp;
    const token = reservation.reservation_token || '';
    let ref = '';
    if(token.length >= 5) {
      ref = `\n\n(ref: ${token.substr(0, 3)}-${token.substr(3, 2)})`;
    }
    let msg = `Olá, meu nome é ${reservation.customer_name} e tenho uma *RESERVA* (id: ${reservation.id}) e gostaria de receber a confirmação!${ref}`;
    const encodedMsg = encodeURIComponent(msg);
    this.wappUrl = `https://wa.me/${phone}?text=${encodedMsg}`;
  }

  private _setTimeAvailabilities(avails: ReservationAvailability[]) {
    this.curDailyRule = ReservationDailyRule.getCurrent(this.reservationDailyRules, this.selectedDay);
    this.curExceptions = ReservationDayException.getCurrent(this.reservationDayExceptions, this.selectedDay);

    let maxPeople = 20;
    if(this.curDailyRule) {
      maxPeople = this.curDailyRule.max_people_per_reservation;
    }

    for(let avail of avails)
      avail.maxPeople = maxPeople;

    this.availPeopleQty = [];
    for(let i = 0; i < maxPeople; i++) {
      this.availPeopleQty.push(i+1);
    }

    this.timeAvailabilities = avails;
    if(this._setErrors(avails)) {
      console.log(this.errors);
    }
  }
  private _setAvailability(avail: ReservationAvailability) {
    this.curAvailability = avail;
    if(this._setErrors([avail])) {
      console.log(this.errors);
    }
  }
  private _setErrors(avails: ReservationAvailability[]): boolean {
    if(!avails)
      return;

    this.errors = null;

    if(avails.length == 0) {
      let err: any = {
        field: 'day',
        message: 'Reservas indisponíveis para este dia',
        availability: null
      };

      if(this.curExceptions && this.curExceptions.length > 0) {
        let count2 = 0;
        let stat = '';
        for(let exc of this.curExceptions) {
          if(exc.status == "closed" || exc.status == "temporarily_closed") {
            count2++;
            stat = exc.status;
          }
        }
        if(count2 == this.curExceptions.length) {
          if(stat == "closed") {
            err.message = "FECHADO";
          } else if(stat == "temporarily_closed") {
            err.message = "FECHADO (POR ENQUANTO)"
          }
          err.message += ": " + this.curExceptions[0].description;
        }
      }

      this.errors = err;
      return true;
    }

    let count = 0;
    const last = avails.length - 1;
    for(let i = 0; i < avails.length; i++) {
      let avail = avails[i];

      // mark as available only if there are at least 2 seats
      let qtyLeft: number = avail.max_to_reserve - avail.total_reserved - avail.total_pending;
      if(qtyLeft - 2 < 0) {
        count++;
        if(this.reservation.building_region_id) {
          this.errors = {
            field: 'region',
            message: "Não há mais vaga para este espaço",
            availability: avail
          };
          return true;
        } else if(this.reservation.schedule) {
          if(i == last && count == last + 1) {
            this.errors = {
              field: 'time',
              message: "Não há mais vaga para este horário",
              availability: avail
            };
            return true;
          } else {
            let br: BuildingRegion = Utils.findById(avail.building_region.id, this.buildingRegions);
            br.full = true;
          }
        } else if(!this.reservation.schedule && (i == last && count == last + 1)) {
          let err: any = {
            field: 'day',
            message: "Não há mais vaga para este dia",
            availability: avail
          };
          if(this.curExceptions && this.curExceptions.length > 0) {
            let count2 = 0;
            for(let exc of this.curExceptions) {
              if(exc.status == "closed") {
                count2++;
              }
            }
            if(count2 == this.curExceptions.length) {
              err.message = "FECHADO: " + this.curExceptions[0].description;
            }
          }
          this.errors = err;
          return true;
        }
      } else if(i == last && this.reservation.building_region_id){
        if(this.availPeopleQty && this.availPeopleQty.length > qtyLeft) {
          this.availPeopleQty = [];
          for(let j = 0; j < qtyLeft; j++) {
            this.availPeopleQty.push(j+1);
          }
        }
      }
    }
    return false;
  }

  private _loadData(company_name: string) {
    this.api.show('companies', company_name).subscribe(
      res => {
        if(JSON.stringify([]) == JSON.stringify(res.company)) {
          alert("Estabelecimento não cadastrado");
          return;
        }
        Company.setAdditionalData(res.company);
        this.company = res.company;
        this._checkLogo();
        this._loadRulesData();
      },
      err => {
        console.log(err);
        alert("Estabelecimento inválido");
      }
    );
  	this.done = true;
  }
  private _loadRulesData() {
    this.api.index('reservation_rules', {
      q: {
        company_id: this.company.id
      },
      o: {
        id: 'desc'
      }
    }).subscribe(
      res => {
        if(res.reservation_rules && res.reservation_rules[0]) {
          this.reservationRule = res.reservation_rules[0];
          this.done = true; // while user reads and accepts terms and conditions, everything else is loaded.
          this._loadBuildingRegions();
          this._loadDailyRulesAndExceptions();
          this._loadAvailabilities(Utils.dateToISO(this.selectedDay));

          setTimeout(() => {
            let el = <HTMLTextAreaElement>document.getElementById("tac");
            if(el && (el.scrollHeight > el.clientHeight)) {
              el.style.height = el.scrollHeight + 20 + "px";
            }
          }, 50);
        } else {
          alert("Erro ao carregar informações de reserva");
        }
      },
      err => {
        console.log(err);
        alert("Erro ao carregar informações de reserva");
      }
    );
  }
  private _loadBuildingRegions() {
    this.api.indexAll('building_regions', {
      q: {
        reservation_rule_id: this.reservationRule.id
      }
    }).subscribe(
      res => {
        BuildingRegion.setArrayAdditionalData(res.building_regions);
        this.buildingRegions = res.building_regions;
      },
      err => {
        console.log(err);
        alert("Erro ao buscar espaços do estabelecimento");
      }
    );
  }
  private _loadDailyRulesAndExceptions() {
    this.api.indexAll('reservation_daily_rules', {
      q: {
        reservation_rule_id: this.reservationRule.id
      }
    }).subscribe(
      res => {
        ReservationDailyRule.setArrayAdditionalData(res.reservation_daily_rules);
        this.reservationDailyRules = res.reservation_daily_rules;
        this.curDailyRule = ReservationDailyRule.getCurrent(this.reservationDailyRules, this.selectedDay);
      },
      err => {
        console.log(err);
        alert("Erro ao carregar informações diárias de reserva");
      }
    );

    let yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);

    this.api.indexAll('reservation_day_exceptions', {
      q: {
        company_id: this.company.id
      },
      q_interval: {
        schedule: ">" + Utils.dateToISO(yesterday)
      }
    }).subscribe(
      res => {
        ReservationDayException.setArrayAditionalData(res.reservation_day_exceptions);
        this.reservationDayExceptions = res.reservation_day_exceptions;
        this.curExceptions = ReservationDayException.getCurrent(this.reservationDayExceptions, this.selectedDay);
      },
      err => {
        console.log(err);
        alert("Erro ao carregar exceções de funcionamento");
      }
    );
  }
  private _loadAvailabilities(date: string) {
    this.api.show('reservations', 'availability', {
      company_id: this.company.id,
      date: date
    }).subscribe(
      res => {
        this.availabilities = res.availability;
        this._getSchedules();
        if(this._setErrors(this.availabilities)) {
          // reservations unavailable. may be full or may be closed or a no-reservation day.
          console.log(this.errors);
        }
      }
    );
  }
  private _getSchedules() {
    this.availSchedules = [];
    const now = new Date();

    for(let avail of this.availabilities) {
      if(avail.max_to_reserve - avail.total_reserved > 1) {
        const availDate = new Date(avail.schedule);
        const diff = availDate.getTime() - now.getTime();
        const disabled = (diff / (1000 * 60 * 60 * 1)) < 1; // reservations must be made at least 1 hour ahead of time.
        
        let sched = {time: avail.schedule.split(" ")[1].substr(0,5), disabled: disabled};
        Utils.pushIfNotExists(this.availSchedules, sched);
      }
    }
  }

  private _checkLogo() {
    let path = '/assets/companies-logo/' + this.company.name;
    if(Utils.fileExists(path)) {
      this.logoPath = path;
    }
  }
}
